import SuperUserOnly from "@/features/navigation/components/SuperUserOnly";
import { canAddAttribute, useMyAccessModes } from "@/features/clinical-access/api/selectMyAccessModes";
import { BuilderAddOption } from "@/features/builder/components/BuilderAddOption";
import { filterClinicianBuilderRows } from "@/features/builder/utils/builderAccess";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
    type VisibilityState,
} from "@tanstack/react-table";
import {
    ArrowUpDown,
    CheckCircle2,
    ChevronDown,
    Copy,
    ExternalLink,
    EyeOff,
    Globe,
    MoreVertical,
    Pencil,
    Plus,
    RefreshCcw,
    Trash2,
    XCircle,
} from "lucide-react";
import Fuse from "fuse.js";
import { ui } from "@patient-watch/ui";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@patient-watch/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@patient-watch/ui/popover";
import { Button } from "@patient-watch/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { formatScheduleSummary } from "@patient-watch/forms";
import { schedulesQueryOptions, useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import { useScheduleValidationStatusesQuery } from "@/features/validations/api/selectValidationStatus";
import ValidationStatusBadge from "@/features/validations/components/ValidationStatusBadge";
import InviteToValidateDialog from "@/features/validations/components/InviteToValidateDialog";
import ValidationHistoryDialog from "@/features/validations/components/ValidationHistoryDialog";
import { useCopyScheduleMutation } from "@/features/schedules/api/copySchedule";
import { useDeleteScheduleMutation, useScheduleDeleteUsageQuery } from "@/features/schedules/api/deleteSchedule";
import { SchedulePublicationDialog } from "@/features/schedules/components/SchedulePublicationDialog";
import type { ScheduleViewT } from "@patient-watch/types";
import { useNav } from "../../../../utils/nav";
import { useAuth } from "@/utils/auth";
import { services as s } from "@/services";
import { supabaseClient } from "@/lib/supabase";
import { AppSearchInput } from "@/components/common/AppSearchInput";
import { DraftScheduleWithAiButton } from "@/features/ai-builder/DraftScheduleWithAiButton";

function DeleteScheduleDialog({ schedule }: { schedule: ScheduleViewT }) {
    const [open, setOpen] = React.useState(false);
    const [replacementScheduleId, setReplacementScheduleId] = React.useState("");
    const { data: schedulesData } = useSchedulesQuery();
    const schedules = schedulesData ?? [];
    const scheduleId = schedule.schedule_id ?? "";
    const usageQuery = useScheduleDeleteUsageQuery(scheduleId, open && Boolean(scheduleId));
    const deleteScheduleMutation = useDeleteScheduleMutation();

    const replacementOptions = schedules.filter((option) => option.schedule_id !== schedule.schedule_id);
    const usage = usageQuery.data;
    const needsReplacement = Boolean(usage && usage.total > 0);
    const canDelete = !needsReplacement || Boolean(replacementScheduleId);
    const scheduleName = formatScheduleSummary(schedule) || "this schedule";

    React.useEffect(() => {
        if (!open) {
            setReplacementScheduleId("");
        }
    }, [open]);

    async function handleDelete() {
        if (!scheduleId) return;

        await deleteScheduleMutation.mutateAsync({
            scheduleId,
            replacementScheduleId: replacementScheduleId || undefined,
        });
        setOpen(false);
    }

    return (
        <ui.Dialog open={open} onOpenChange={setOpen}>
            <ui.DialogTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto text-red-600 hover:text-red-700"
                >
                    <Trash2 className="w-4 h-4" />
                    Delete
                </Button>
            </ui.DialogTrigger>
            <ui.DialogContent className="max-w-lg">
                <ui.DialogHeader>
                    <ui.DialogTitle>Delete schedule?</ui.DialogTitle>
                    <ui.DialogDescription>
                        This will delete {scheduleName}. If it is already used, choose a replacement first.
                    </ui.DialogDescription>
                </ui.DialogHeader>

                {usageQuery.isLoading ? (
                    <div className="py-6 text-muted-foreground text-sm">Checking where this schedule is used...</div>
                ) : usageQuery.isError ? (
                    <div className="py-6 text-red-600 text-sm">
                        Could not check schedule usage. {usageQuery.error.message}
                    </div>
                ) : usage ? (
                    <div className="space-y-4">
                        <div className="bg-slate-50 dark:bg-slate-800 p-3 border rounded-md text-sm">
                            <div className="font-medium">Used in {usage.total} place(s)</div>
                            <ul className="space-y-1 mt-2 text-muted-foreground">
                                <li>{usage.trackerCount} tracker(s)</li>
                                <li>{usage.backupTrackerCount} backup tracker schedule(s)</li>
                                <li>{usage.entryCount} generated entry/entries</li>
                                <li>{usage.templateCount} template setting(s)</li>
                            </ul>
                        </div>

                        {needsReplacement ? (
                            <label className="block space-y-2 text-sm">
                                <span className="font-medium">Replace usages with</span>
                                <select
                                    value={replacementScheduleId}
                                    onChange={(event) => setReplacementScheduleId(event.target.value)}
                                    className="bg-white dark:bg-slate-900 px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-md w-full text-sm"
                                    disabled={replacementOptions.length === 0}
                                >
                                    <option value="">Select replacement schedule</option>
                                    {replacementOptions.map((option) => (
                                        <option key={option.schedule_id} value={option.schedule_id}>
                                            {formatScheduleSummary(option)}
                                        </option>
                                    ))}
                                </select>
                                {replacementOptions.length === 0 && (
                                    <span className="text-red-600 text-xs">
                                        There are no other schedules available to replace these usages.
                                    </span>
                                )}
                            </label>
                        ) : (
                            <div className="text-muted-foreground text-sm">
                                This schedule is not currently used, so it can be deleted without replacement.
                            </div>
                        )}
                    </div>
                ) : null}

                <ui.DialogFooter>
                    <ui.DialogClose asChild>
                        <fc.CancelButton variant="outline" size="default" className="w-auto" />
                    </ui.DialogClose>
                    <fc.DeleteButton
                        variant="destructive"
                        size="default"
                        onClick={handleDelete}
                        disabled={
                            usageQuery.isLoading ||
                            usageQuery.isError ||
                            !canDelete ||
                            (needsReplacement && replacementOptions.length === 0)
                        }
                        isLoading={deleteScheduleMutation.isPending}
                        label={needsReplacement ? "Replace and delete" : "Delete"}
                        className="w-auto"
                    />
                </ui.DialogFooter>
            </ui.DialogContent>
        </ui.Dialog>
    );
}

function ScheduleActionsCell({ schedule }: { schedule: ScheduleViewT }) {
    const navigate = useNavigate();
    const auth = useAuth();
    const copyScheduleMutation = useCopyScheduleMutation();
    const [publicationOpen, setPublicationOpen] = React.useState(false);
    const { data: accessModes } = useMyAccessModes();
    const canAddSchedules = auth.roles.isAdmin || canAddAttribute(accessModes, "schedules");

    const handleDuplicate = async () => {
        if (!schedule.schedule_id || !canAddSchedules) return;

        const result = await copyScheduleMutation.mutateAsync({
            scheduleId: schedule.schedule_id,
        });
        if (result) {
            void navigate({
                to: "/builder/schedules/$scheduleId",
                params: { scheduleId: result.schedule_id },
            });
        }
    };

    return (
        <div className="flex items-center gap-2">
            <Popover>
                <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <MoreVertical className="w-4 h-4" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="p-1 w-64">
                    <div className="flex flex-col">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                            asChild
                        >
                            <Link
                                to="/schedules/$scheduleId"
                                target="_blank"
                                params={{
                                    scheduleId: schedule.schedule_id || "",
                                }}
                            >
                                <ExternalLink className="w-4 h-4" />
                                View
                            </Link>
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                            asChild
                        >
                            <Link
                                to="/builder/schedules/$scheduleId"
                                params={{
                                    scheduleId: schedule.schedule_id || "",
                                }}
                            >
                                <Pencil className="w-4 h-4" />
                                Edit
                            </Link>
                        </Button>
                        {canAddSchedules ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                                onClick={handleDuplicate}
                                disabled={copyScheduleMutation.isPending}
                            >
                                <Copy className="w-4 h-4" />
                                {copyScheduleMutation.isPending ? "Duplicating..." : "Duplicate"}
                            </Button>
                        ) : null}
                        {auth.roles.isAdmin ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                                onClick={() => setPublicationOpen(true)}
                            >
                                {schedule.is_published ? <EyeOff className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                                {schedule.is_published ? "Unpublish" : "Publish"}
                            </Button>
                        ) : null}
                        <InviteToValidateDialog entityType="schedule" entityId={schedule.schedule_id ?? ""} />
                        <ValidationHistoryDialog entityType="schedule" entityId={schedule.schedule_id ?? ""} />
                        <DeleteScheduleDialog schedule={schedule} />
                    </div>
                </PopoverContent>
            </Popover>
            {auth.roles.isAdmin && schedule.schedule_id ? (
                <SchedulePublicationDialog
                    scheduleId={schedule.schedule_id}
                    isPublished={schedule.is_published ?? false}
                    open={publicationOpen}
                    onOpenChange={setPublicationOpen}
                />
            ) : null}
        </div>
    );
}

function ScheduleValidationStatusCell({ scheduleId }: { scheduleId: string }) {
    const { data: statuses } = useScheduleValidationStatusesQuery();
    return <ValidationStatusBadge status={statuses?.[scheduleId]} />;
}

export const columns: ColumnDef<ScheduleViewT>[] = [
    {
        id: "derived_name",
        accessorFn: (row) => formatScheduleSummary(row),
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    size="sm"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Schedule
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                </ui.Button>
            );
        },
        cell: ({ row }) => {
            const schedule = row.original;
            const derivedName = formatScheduleSummary(schedule);

            return (
                <div className="max-w-md">
                    <div className="font-medium truncate">{derivedName}</div>
                </div>
            );
        },
    },
    {
        accessorKey: "is_published",
        header: "Published",
        cell: ({ row }) => {
            const isPublished = row.getValue("is_published") as boolean;
            return isPublished ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
                <XCircle className="w-4 h-4 text-gray-400" />
            );
        },
    },
    {
        accessorKey: "timings",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    size="sm"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Timings
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                </ui.Button>
            );
        },
        cell: ({ row }) => {
            const timings = row.getValue("timings") as any[];
            return <div className="text-center">{timings?.length || 0}</div>;
        },
    },
    {
        accessorKey: "created_at",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    size="sm"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Created
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                </ui.Button>
            );
        },
        cell: ({ row }) => {
            const date = row.getValue("created_at") as string;
            return <div className="text-muted-foreground text-sm">{new Date(date).toLocaleDateString()}</div>;
        },
    },
    {
        id: "validation",
        header: "Validation",
        cell: ({ row }) => <ScheduleValidationStatusCell scheduleId={row.original.schedule_id ?? ""} />,
    },
    {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => <ScheduleActionsCell schedule={row.original} />,
    },
];

function SchedulesPage() {
    useNav([
        {
            label: "Builder",
            route: { to: "/builder" },
        },
        {
            label: "Schedules",
            route: {
                to: "/builder/schedules",
            },
        },
    ]);

    const navigate = useNavigate();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const { data: accessModes } = useMyAccessModes();
    const canAddSchedules = auth.roles.isAdmin || canAddAttribute(accessModes, "schedules");
    const { data, refetch, isFetching } = useSchedulesQuery();
    const isAdmin = auth.roles.isAdmin;
    const userId = auth.id;
    const schedules = React.useMemo(
        () => filterClinicianBuilderRows(data ?? [], { isAdmin, userId }),
        [data, isAdmin, userId]
    );
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = React.useState({});
    const [isCreatingSchedule, setIsCreatingSchedule] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");

    const fuse = React.useMemo(
        () =>
            new Fuse(schedules, {
                keys: [
                    "schedule_name",
                    "schedule_description",
                    { name: "derivedName", getFn: (schedule) => formatScheduleSummary(schedule) },
                    {
                        name: "timingLabels",
                        getFn: (schedule) =>
                            (schedule.timings ?? [])
                                .map((timing) => timing.label ?? "")
                                .filter(Boolean)
                                .join(" "),
                    },
                ],
                threshold: 0.4,
                ignoreLocation: true,
            }),
        [schedules]
    );

    const filteredSchedules = React.useMemo(() => {
        const query = searchQuery.trim();
        if (!query) return schedules;
        return fuse.search(query).map((result) => result.item);
    }, [fuse, schedules, searchQuery]);

    const table = useReactTable({
        data: filteredSchedules,
        columns,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
        },
    });

    async function handleRefreshSchedules() {
        await queryClient.invalidateQueries({ queryKey: ["schedules"] });
        await refetch();
    }

    async function handleNewSchedule() {
        if (!canAddSchedules || isCreatingSchedule) return;

        setIsCreatingSchedule(true);
        const newScheduleId = s.uuidv4();
        const { error: insertError } = await supabaseClient.from("schedules").insert({
            schedule_id: newScheduleId,
            schedule_name: "New Schedule",
            schedule_description: "",
            is_published: false,
            author: auth.id,
        });

        if (insertError) {
            console.error("Failed to create schedule:", insertError);
            setIsCreatingSchedule(false);
            return;
        }

        await queryClient.invalidateQueries({ queryKey: ["schedules"] });
        void navigate({
            to: "/builder/schedules/$scheduleId",
            params: { scheduleId: newScheduleId },
        });
    }

    return (
        <div className="p-4 w-full">
            <div className="flex items-center gap-2 mb-4">
                <div className="flex flex-1 items-center gap-2">
                    <AppSearchInput
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Search schedules..."
                        aria-label="Search schedules"
                        clearAriaLabel="Clear schedule search"
                    />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRefreshSchedules}
                        disabled={isFetching}
                        className="gap-2"
                    >
                        <RefreshCcw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
                        {isFetching ? "Refreshing..." : "Refresh"}
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <ui.Button size="sm" variant="outline">
                                Columns <ChevronDown />
                            </ui.Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {table
                                .getAllColumns()
                                .filter((column) => column.getCanHide())
                                .map((column) => {
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={column.id}
                                            className="capitalize"
                                            checked={column.getIsVisible()}
                                            onCheckedChange={(value) => column.toggleVisibility(!!value)}
                                        >
                                            {column.id.replace(/_/g, " ")}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                {canAddSchedules ? (
                    <div className="flex items-center gap-2">
                        <SuperUserOnly>
                            <DraftScheduleWithAiButton />
                        </SuperUserOnly>
                        <Button size="sm" disabled={isCreatingSchedule} onClick={handleNewSchedule} className="gap-2">
                            <Plus className="w-4 h-4" />
                            {isCreatingSchedule ? "Creating..." : "New Schedule"}
                        </Button>
                    </div>
                ) : (
                    <BuilderAddOption kind="schedule" />
                )}
            </div>
            <div className="bg-white dark:bg-slate-900 border rounded-md overflow-hidden">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => {
                                const scheduleId = row.original.schedule_id;
                                return (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                        className={
                                            scheduleId
                                                ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                                                : undefined
                                        }
                                        onClick={() => {
                                            if (!scheduleId) return;
                                            void navigate({
                                                to: "/builder/schedules/$scheduleId",
                                                params: { scheduleId },
                                            });
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                onClick={
                                                    cell.column.id === "actions" || cell.column.id === "validation"
                                                        ? (event) => event.stopPropagation()
                                                        : undefined
                                                }
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No schedules found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center py-4">
                <div className="text-muted-foreground text-sm">
                    {searchQuery.trim()
                        ? `${filteredSchedules.length} of ${schedules.length} schedule(s)`
                        : `${schedules.length} schedule(s) total`}
                </div>
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_app/builder/schedules/")({
    component: SchedulesPage,
    loader: async ({ context }) => {
        const schedules = await context?.queryClient.ensureQueryData(schedulesQueryOptions());
        return { schedules };
    },
});
